<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Kiểm tra hoa hồng V2'">
          <template v-slot:preview>
            <b-alert show variant="info" class="mb-5">
              Số liệu ở màn này lấy từ
              <strong>đúng engine đang chạy báo cáo hoa hồng V2</strong>, nên
              kiểm tra ra bao nhiêu thì báo cáo ra bấy nhiêu — không còn cảnh hai
              màn hai số.
            </b-alert>

            <b-tabs v-model="activeTab" content-class="mt-4">
              <!-- ==================== TAB 1: THEO SẢN PHẨM ==================== -->
              <b-tab title="Kiểm tra sản phẩm">
                <b-form class="mb-5">
                  <b-row class="mb-3">
                    <b-col md="4">
                      <label class="mb-1">Loại hoa hồng:</label>
                      <b-form-select
                        size="sm"
                        v-model="productForm.type"
                        :options="commissionTypeOptions"
                      ></b-form-select>
                    </b-col>
                    <b-col md="8">
                      <label class="mb-1">Sản phẩm:</label>
                      <Autosuggest
                        :key="'p-product-' + componentKey"
                        :model="productForm.productName"
                        :suggestions="productSuggestions"
                        placeholder="sản phẩm"
                        :limit="10"
                        @select="onSelectProduct($event)"
                        @change="onProductInputChange"
                        suggestionName="suggestionName"
                      />
                    </b-col>
                  </b-row>
                  <b-row class="mb-3">
                    <b-col md="5" v-if="productForm.type === COMMISSION_TYPE.STORE">
                      <label class="mb-1">Cửa hàng:</label>
                      <Autosuggest
                        :key="'p-store-' + componentKey"
                        :model="productForm.storeName"
                        :suggestions="filteredStoreOptions"
                        placeholder="cửa hàng"
                        :limit="10"
                        @select="onSelectStore($event)"
                        @change="onStoreInputChange"
                        suggestionName="suggestionName"
                      />
                    </b-col>
                    <b-col md="5" v-else>
                      <label class="mb-1">Bộ phận:</label>
                      <b-form-select
                        size="sm"
                        v-model="productForm.departmentId"
                        :options="departmentOptions"
                        value-field="id"
                        text-field="name"
                      >
                        <template v-slot:first>
                          <b-form-select-option :value="null"
                            >-- Tất cả bộ phận --</b-form-select-option
                          >
                        </template>
                      </b-form-select>
                    </b-col>
                    <b-col md="2">
                      <label class="mb-1">Từ ngày:</label>
                      <date-picker
                        v-model="productForm.fromDate"
                        placeholder="Từ ngày"
                        class="form-control form-control-sm"
                        :config="dpConfigs.date"
                      ></date-picker>
                    </b-col>
                    <b-col md="2">
                      <label class="mb-1">Đến ngày:</label>
                      <date-picker
                        v-model="productForm.toDate"
                        placeholder="Đến ngày"
                        class="form-control form-control-sm"
                        :config="dpConfigs.date"
                      ></date-picker>
                    </b-col>
                    <b-col md="3" class="d-flex align-items-end">
                      <b-button
                        variant="primary"
                        size="sm"
                        class="mr-2"
                        :disabled="productLoading"
                        @click="checkProduct"
                      >
                        <i class="fas fa-search mr-1"></i>Kiểm tra
                      </b-button>
                      <b-button variant="secondary" size="sm" @click="resetProduct">
                        <i class="fas fa-undo mr-1"></i>Đặt lại
                      </b-button>
                    </b-col>
                  </b-row>
                </b-form>

                <vcl-table v-if="productLoading" :columns="5"></vcl-table>

                <div v-else-if="productResult">
                  <!-- Cảnh báo: vì sao sản phẩm không ra tiền / bị lấy nhóm khác -->
                  <b-alert
                    v-for="(warning, index) in productResult.warnings"
                    :key="'pw-' + index"
                    show
                    :variant="warning.level"
                    class="mb-4"
                  >
                    {{ warning.message }}
                  </b-alert>

                  <div class="check-box mb-4">
                    <div class="check-box__title">
                      {{ productResult.product.productName }}
                    </div>
                    <div class="check-box__meta">
                      Mã SP: <strong>{{ productResult.product.productCode }}</strong>
                      &nbsp;·&nbsp; Danh mục:
                      <strong>{{ productResult.product.productCategory }}</strong>
                    </div>
                  </div>

                  <!-- Nhóm hoa hồng chứa sản phẩm, kèm nhóm THẮNG theo §4.11 -->
                  <p class="font-weight-bold mb-2">
                    Nhóm hoa hồng chứa sản phẩm ({{ productResult.categories.length }}):
                  </p>
                  <b-table
                    class="table-bordered table-hover mb-5"
                    :fields="categoryFields"
                    :items="productResult.categories"
                    show-empty
                    empty-text="Sản phẩm không thuộc nhóm hoa hồng nào"
                  >
                    <template v-slot:cell(name)="row">
                      <span>{{ row.item.name }}</span>
                      <b-badge
                        v-if="row.item.id === productResult.winningCategoryId"
                        variant="success"
                        class="ml-2"
                        >Nhóm áp dụng</b-badge
                      >
                    </template>
                    <template v-slot:cell(viaName)="row">
                      <b-badge
                        :variant="row.item.via === 'product' ? 'primary' : 'secondary'"
                        >{{ row.item.viaName }}</b-badge
                      >
                    </template>
                    <template v-slot:cell(createdAt)="row">
                      {{ displayDate(row.item.createdAt) }}
                    </template>
                  </b-table>

                  <!-- Chương trình + bậc điều kiện + mức theo vị trí -->
                  <p class="font-weight-bold mb-2">
                    Chương trình áp dụng ({{ productResult.programs.length }}):
                  </p>
                  <b-alert
                    v-if="!productResult.programs.length"
                    show
                    variant="secondary"
                    class="mb-0"
                  >
                    Không có chương trình hoa hồng nào áp cho sản phẩm này trong
                    phạm vi và kỳ đã chọn.
                  </b-alert>

                  <div
                    v-for="program in productResult.programs"
                    :key="program.commissionProgramId"
                    class="program-block mb-5"
                  >
                    <div class="program-block__head">
                      {{ program.commissionProgramName }}
                      <span class="program-block__date">
                        ({{ displayDate(program.startDate) }} —
                        {{ displayDate(program.endDate) }})
                      </span>
                    </div>

                    <template v-for="(detail, index) in program.details">
                      <!-- Dải ngăn giữa phần TÍNH TIỀN và phần BỊ BỎ QUA -->
                      <div
                        v-if="isFirstIgnored(program.details, index)"
                        :key="'sep-' + detail.commissionProgramDetailId"
                        class="detail-divider"
                      >
                        Các bậc bên dưới thuộc nhóm không thắng — engine BỎ QUA,
                        không tính tiền (§4.11)
                      </div>
                      <div
                        :key="detail.commissionProgramDetailId"
                        class="detail-block"
                        :class="{ 'detail-block--ignored': !detail.isWinningCategory }"
                      >
                        <div class="detail-block__head">
                          <span class="mr-3">
                            Nhóm: <strong>{{ detail.commissionCategoryName }}</strong>
                          </span>
                          <span class="mr-3">
                            Điều kiện: <strong>{{ detail.conditionText }}</strong>
                          </span>
                          <b-badge
                            v-if="detail.isWinningCategory"
                            variant="success"
                            >Đang tính</b-badge
                          >
                        </div>
                        <b-table
                          class="table-bordered table-hover mb-0"
                          :fields="rateFields"
                          :items="detail.employeeRates"
                          show-empty
                          empty-text="Bậc này chưa cấu hình mức cho vị trí nào"
                          small
                        >
                          <template v-slot:cell(value)="row">
                            <span v-if="row.item.rateType === RATE_TYPE.MONEY">
                              {{ displayAmount(row.item.amount) }}
                            </span>
                            <span v-else>{{ row.item.percent }}%</span>
                          </template>
                        </b-table>
                      </div>
                    </template>
                  </div>
                </div>
              </b-tab>

              <!-- ==================== TAB 2: THEO HOÁ ĐƠN ==================== -->
              <b-tab title="Kiểm tra theo số hoá đơn">
                <b-form class="mb-5" @submit.prevent="checkBill">
                  <b-row>
                    <b-col md="4">
                      <label class="mb-1">Số hoá đơn:</label>
                      <b-form-input
                        size="sm"
                        v-model="billForm.billNumber"
                        placeholder="Nhập số hoá đơn"
                        @keyup.enter="checkBill"
                      ></b-form-input>
                    </b-col>
                    <b-col md="3" class="d-flex align-items-end">
                      <b-button
                        variant="primary"
                        size="sm"
                        class="mr-2"
                        :disabled="billLoading"
                        @click="checkBill"
                      >
                        <i class="fas fa-search mr-1"></i>Kiểm tra
                      </b-button>
                      <b-button variant="secondary" size="sm" @click="resetBill">
                        <i class="fas fa-undo mr-1"></i>Đặt lại
                      </b-button>
                    </b-col>
                  </b-row>
                </b-form>

                <vcl-table v-if="billLoading" :columns="6"></vcl-table>

                <div v-else-if="billResult">
                  <b-alert
                    v-for="(warning, index) in billResult.warnings"
                    :key="'bw-' + index"
                    show
                    :variant="warning.level"
                    class="mb-4"
                  >
                    {{ warning.message }}
                  </b-alert>

                  <div class="check-box mb-4">
                    <div class="check-box__title">
                      Hoá đơn {{ billResult.bill.billNumber }}
                    </div>
                    <div class="check-box__meta">
                      Cửa hàng: <strong>{{ billResult.bill.storeName }}</strong>
                      &nbsp;·&nbsp; Ngày:
                      <strong>{{
                        displayDate(billResult.bill.billDate)
                      }}</strong>
                      &nbsp;·&nbsp; Tổng hoa hồng:
                      <strong class="text-primary">{{
                        displayAmount(billResult.totals.amount)
                      }}</strong>
                    </div>
                  </div>

                  <!-- Gom theo người nhận: ai được bao nhiêu -->
                  <p class="font-weight-bold mb-2">
                    Hoa hồng theo nhân viên ({{ billResult.byEmployee.length }}):
                  </p>
                  <b-table
                    class="table-bordered table-hover mb-5"
                    :fields="employeeFields"
                    :items="billResult.byEmployee"
                    show-empty
                    empty-text="Hoá đơn không phát sinh hoa hồng"
                  >
                    <template v-slot:cell(employeeName)="row">
                      {{ row.item.employeeName }}
                      <span class="text-muted">({{ row.item.employeeCode }})</span>
                    </template>
                    <template v-slot:cell(amount)="row">
                      <span :class="amountClass(row.item.amount)">
                        {{ displayAmount(row.item.amount) }}
                      </span>
                    </template>
                  </b-table>

                  <!-- Chi tiết từng dòng, GOM THEO SẢN PHẨM cho dễ đọc:
                       tên SP / doanh thu / nhóm / chương trình vốn giống nhau
                       trên mọi dòng của cùng một SP nên đưa hết lên đầu khối,
                       bảng bên dưới chỉ còn ai được bao nhiêu. -->
                  <p class="font-weight-bold mb-2">
                    Chi tiết từng dòng ({{ billResult.rows.length }}) —
                    {{ billProductGroups.length }} sản phẩm:
                  </p>
                  <b-alert
                    v-if="!billProductGroups.length"
                    show
                    variant="secondary"
                    class="mb-0"
                  >
                    Không có dòng hoa hồng
                  </b-alert>

                  <div
                    v-for="group in billProductGroups"
                    :key="group.key"
                    class="product-block mb-4"
                  >
                    <div class="product-block__head">
                      <div class="product-block__name">
                        {{ group.productName }}
                        <b-badge
                          :variant="group.direction === -1 ? 'danger' : 'secondary'"
                          class="ml-2"
                          >{{ group.sourceTypeName }}</b-badge
                        >
                      </div>
                      <div class="product-block__total" :class="amountClass(group.amount)">
                        {{ displayAmount(group.amount) }}
                      </div>
                    </div>
                    <div class="product-block__meta">
                      Doanh thu: <strong>{{ displayAmount(group.revenue) }}</strong>
                      &nbsp;·&nbsp; SL: <strong>{{ group.quantity }}</strong>
                      &nbsp;·&nbsp; Nhóm HH:
                      <strong>{{ group.commissionCategoryName }}</strong>
                      &nbsp;·&nbsp; Chương trình:
                      <strong>{{ group.commissionProgramName }}</strong>
                    </div>
                    <b-table
                      class="table-bordered table-hover mb-0"
                      :fields="rowFields"
                      :items="group.rows"
                      small
                    >
                      <template v-slot:cell(employeeName)="row">
                        {{ row.item.employeeName }}
                        <span class="text-muted">({{ row.item.employeeCode }})</span>
                      </template>
                      <template v-slot:cell(rateValue)="row">
                        <span v-if="row.item.rateType === RATE_TYPE.MONEY">
                          {{ displayAmount(row.item.rateValue) }}
                        </span>
                        <span v-else>{{ row.item.rateValue }}%</span>
                      </template>
                      <template v-slot:cell(signedAmount)="row">
                        <span :class="amountClass(row.item.signedAmount)">
                          {{ displayAmount(row.item.signedAmount) }}
                        </span>
                      </template>
                    </b-table>
                  </div>
                </div>
              </b-tab>
            </b-tabs>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Kiểm tra hoa hồng V2 — bản mới của màn /reward/reward-check.
 *
 * Khác màn cũ ở chỗ mọi con số đến từ CHÍNH commission-engine đang chạy báo cáo
 * (KT-14), nên kiểm tra xong là tin được. Ngoài ra chi tiết hơn:
 *   - Nói rõ sản phẩm vào nhóm bằng đường nào (khai đích danh / qua danh mục)
 *   - Chỉ ra nhóm nào THẮNG khi sản phẩm nằm nhiều nhóm (§4.11), bậc nào bị bỏ qua
 *   - Tab hoá đơn tách rõ từng nguồn (bán hàng / trả hàng / ...) và mức áp cho từng dòng
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import debounce from 'debounce';
import moment from 'moment';
import { VclTable } from 'vue-content-loading';
import { TIME_TRIGGER } from '@/utils/constants';
import { makeToastFaile, removeAccents } from '@/utils/common';
// NT-3: dùng lại helper định dạng / ngày tháng của màn báo cáo, không viết lại
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

/** Trùng ENUM.COMMISSION_PROGRAM_TYPE bên API */
const COMMISSION_TYPE = { STORE: 1, CR: 3 };
/** Trùng ENUM.COMMISSION_RATE_TYPE bên API */
const RATE_TYPE = { MONEY: 1, PERCENT: 2 };

export default {
  components: { KTCodePreview, Autosuggest, VclTable, datePicker },
  mixins: [commissionReportMixin],
  data() {
    return {
      COMMISSION_TYPE,
      RATE_TYPE,
      activeTab: 0,
      componentKey: 0,

      commissionTypeOptions: [
        { value: COMMISSION_TYPE.STORE, text: 'Ngành cửa hàng' },
        { value: COMMISSION_TYPE.CR, text: 'CR' },
      ],

      // --- Tab sản phẩm ---
      productForm: {
        type: COMMISSION_TYPE.STORE,
        productId: null,
        productName: '',
        storeId: null,
        storeName: '',
        departmentId: null,
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().format(DATE_INPUT_FORMAT),
      },
      productSuggestions: [],
      productLoading: false,
      productResult: null,

      // --- Tab hoá đơn ---
      billForm: { billNumber: '' },
      billLoading: false,
      billResult: null,

      storeOptions: [],
      filteredStoreOptions: [],
      departmentOptions: [],

      categoryFields: [
        { key: 'name', label: 'Nhóm hoa hồng', thStyle: thStyle('45%') },
        { key: 'viaName', label: 'Vào nhóm bằng', thStyle: thStyle('25%') },
        { key: 'createdAt', label: 'Ngày tạo nhóm', thStyle: thStyle('30%') },
      ],
      rateFields: [
        { key: 'jobTitleName', label: 'Vị trí', thStyle: thStyle('50%') },
        { key: 'rateTypeName', label: 'Loại mức', thStyle: thStyle('25%') },
        {
          key: 'value',
          label: 'Giá trị',
          thStyle: thStyle('25%'),
          tdClass: 'text-right',
        },
      ],
      employeeFields: [
        { key: 'employeeName', label: 'Nhân viên', thStyle: thStyle('40%') },
        { key: 'jobTitleName', label: 'Vai trò', thStyle: thStyle('30%') },
        {
          key: 'quantity',
          label: 'Số lượng',
          thStyle: thStyle('15%'),
          tdClass: 'text-center',
        },
        {
          key: 'amount',
          label: 'Hoa hồng',
          thStyle: thStyle('15%'),
          tdClass: 'text-right',
        },
      ],
      // Cột trong khối sản phẩm: SP / nguồn / nhóm / chương trình / doanh thu
      // đã nằm ở header khối nên không lặp lại ở từng dòng nữa.
      rowFields: [
        { key: 'employeeName', label: 'Nhân viên', thStyle: thStyle('40%') },
        { key: 'jobTitleName', label: 'Vai trò', thStyle: thStyle('25%') },
        {
          key: 'signedQuantity',
          label: 'SL',
          thStyle: thStyle('10%'),
          tdClass: 'text-center',
        },
        {
          key: 'rateValue',
          label: 'Mức áp',
          thStyle: thStyle('12%'),
          tdClass: 'text-right',
        },
        {
          key: 'signedAmount',
          label: 'Thành tiền',
          thStyle: thStyle('13%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    /**
     * Gom dòng hoa hồng theo (sản phẩm + nguồn): mọi dòng của cùng một sản phẩm
     * dùng chung tên SP / doanh thu / nhóm / chương trình, chỉ khác người nhận.
     * Tách nguồn riêng để dòng bán và dòng trả hàng của cùng SP không lẫn vào nhau.
     */
    billProductGroups() {
      if (!this.billResult) {
        return [];
      }
      const groupByKey = new Map();
      this.billResult.rows.forEach((row) => {
        const key = `${row.productId}|${row.sourceType}|${row.commissionProgramDetailId}`;
        if (!groupByKey.has(key)) {
          groupByKey.set(key, {
            key,
            productName: row.productName,
            sourceTypeName: row.sourceTypeName,
            direction: row.direction,
            revenue: row.revenue,
            commissionCategoryName: row.commissionCategoryName,
            commissionProgramName: row.commissionProgramName,
            quantity: 0,
            amount: 0,
            rows: [],
          });
        }
        const group = groupByKey.get(key);
        // SL của SP là số lượng bán, KHÔNG cộng dồn theo từng người nhận
        group.quantity = row.signedQuantity;
        group.amount += row.signedAmount;
        group.rows.push(row);
      });
      return Array.from(groupByKey.values());
    },
  },
  created() {
    this.fetchStores();
    this.fetchDepartments();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng', route: '/commission/categories' },
      { title: 'Kiểm tra hoa hồng V2' },
    ]);
  },
  methods: {
    /** true tại bậc BỊ BỎ QUA đầu tiên — chỗ chèn dải ngăn hai phần */
    isFirstIgnored(details, index) {
      return (
        !details[index].isWinningCategory &&
        (index === 0 || details[index - 1].isWinningCategory)
      );
    },

    // ----- Nạp danh mục dùng chung -----
    fetchStores() {
      ApiService.setHeader();
      ApiService.get('/stores/getStores').then((response) => {
        this.storeOptions = (response.data.data || []).map((item) => ({
          id: item.id,
          name: item.name,
          suggestionName: item.name,
        }));
        this.filteredStoreOptions = [...this.storeOptions];
      });
    },
    fetchDepartments() {
      ApiService.setHeader();
      ApiService.get('/employees/departments').then((response) => {
        this.departmentOptions = response.data.data || [];
      });
    },

    // ----- Autosuggest sản phẩm -----
    onProductInputChange(text) {
      this.productForm.productName = text;
      if (!text) {
        this.productForm.productId = null;
      }
      this.debounceSearchProduct(text);
    },
    debounceSearchProduct: debounce(function (name) {
      this.searchProduct(name);
    }, TIME_TRIGGER),
    searchProduct(name) {
      if (!name) {
        this.productSuggestions = [];
        return;
      }
      ApiService.query('product/searchProductByName', { params: { name } }).then(
        (response) => {
          this.productSuggestions = (response.data.data || []).map((item) => ({
            id: item.id,
            name: item.productName,
            code: item.productCode,
            suggestionName: item.productCode
              ? `${item.productCode} - ${item.productName}`
              : item.productName,
          }));
        },
      );
    },
    onSelectProduct(option) {
      this.productForm.productId = option.item.id;
      this.productForm.productName = option.item.name;
    },

    // ----- Autosuggest cửa hàng -----
    onStoreInputChange(text) {
      this.productForm.storeName = text;
      if (!text) {
        this.productForm.storeId = null;
        this.filteredStoreOptions = [...this.storeOptions];
        return;
      }
      const keyword = removeAccents(text).toLowerCase();
      this.filteredStoreOptions = this.storeOptions.filter(
        (item) => removeAccents(item.name).toLowerCase().indexOf(keyword) > -1,
      );
    },
    onSelectStore(option) {
      this.productForm.storeId = option.item.id;
      this.productForm.storeName = option.item.name;
    },

    // ----- Tab 1: kiểm tra sản phẩm -----
    checkProduct() {
      if (!this.productForm.productId) {
        return makeToastFaile('Vui lòng chọn sản phẩm');
      }
      if (
        this.productForm.type === COMMISSION_TYPE.STORE &&
        !this.productForm.storeId
      ) {
        return makeToastFaile('Vui lòng chọn cửa hàng');
      }
      this.productLoading = true;
      this.productResult = null;

      ApiService.query('commission-checks/product', {
        params: {
          productId: this.productForm.productId,
          type: this.productForm.type,
          storeId: this.productForm.storeId,
          departmentId: this.productForm.departmentId,
          fromDate: this.productForm.fromDate,
          toDate: this.productForm.toDate,
        },
      })
        .then((response) => {
          this.productResult = response.data.data;
        })
        .catch((error) => {
          makeToastFaile(this.getErrorMessage(error, 'Không kiểm tra được sản phẩm'));
        })
        .finally(() => {
          this.productLoading = false;
        });
    },
    resetProduct() {
      this.productForm.productId = null;
      this.productForm.productName = '';
      this.productForm.storeId = null;
      this.productForm.storeName = '';
      this.productForm.departmentId = null;
      this.productResult = null;
      this.productSuggestions = [];
      this.filteredStoreOptions = [...this.storeOptions];
      this.componentKey += 1;
    },

    // ----- Tab 2: kiểm tra hoá đơn -----
    checkBill() {
      const billNumber = (this.billForm.billNumber || '').trim();
      if (!billNumber) {
        return makeToastFaile('Vui lòng nhập số hoá đơn');
      }
      this.billLoading = true;
      this.billResult = null;

      ApiService.query('commission-checks/bill', { params: { billNumber } })
        .then((response) => {
          this.billResult = response.data.data;
        })
        .catch((error) => {
          makeToastFaile(this.getErrorMessage(error, 'Không kiểm tra được hoá đơn'));
        })
        .finally(() => {
          this.billLoading = false;
        });
    },
    resetBill() {
      this.billForm.billNumber = '';
      this.billResult = null;
    },
  },
};
</script>

<style scoped>
.check-box {
  background: #f8f9fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px 16px;
}
.check-box__title {
  font-size: 15px;
  font-weight: 600;
  color: #181c32;
}
.check-box__meta {
  font-size: 13px;
  color: #7e8299;
  margin-top: 4px;
}
.program-block {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  overflow: hidden;
}
.program-block__head {
  background: #f3f6f9;
  padding: 10px 16px;
  font-weight: 600;
  color: #181c32;
}
.program-block__date {
  font-weight: 400;
  color: #7e8299;
  font-size: 13px;
}
.detail-block {
  padding: 12px 16px;
  border-top: 1px solid #ebedf3;
}
.product-block {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  overflow: hidden;
}
.product-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #f3f6f9;
  padding: 10px 16px;
}
.product-block__name {
  font-weight: 600;
  color: #181c32;
  font-size: 13px;
}
.product-block__total {
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
}
.product-block__meta {
  font-size: 12px;
  color: #7e8299;
  padding: 8px 16px;
  border-top: 1px solid #ebedf3;
  border-bottom: 1px solid #ebedf3;
}
.detail-divider {
  background: #fff4de;
  color: #b8860b;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 16px;
  border-top: 1px solid #ebedf3;
}
.detail-block--ignored {
  background: #fffaf0;
  opacity: 0.75;
}
.detail-block__head {
  font-size: 13px;
  margin-bottom: 8px;
}
</style>
